import { ref, computed, watch, nextTick } from 'vue'
import { defineStore } from 'pinia'
import { authService } from '@/services/auth'
import { createTokenRefreshService, type TokenRefreshService } from '@/services/tokenRefresh'
import { createMultiTabSyncService, type MultiTabSyncService } from '@/services/multiTabSync'
import { csrfService } from '@/services/csrfService'
import type { User, LoginRequest, RefreshTokenResponse } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  // State with backup mechanism for Pinia installation timing issues
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Helper to create a safe user copy with all required properties
  const createUserCopy = (userData: User): User => {
    return {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      company_id: userData.company_id,
      company: userData.company,
      is_active: userData.is_active,
      last_login_at: userData.last_login_at,
      created_at: userData.created_at,
      updated_at: userData.updated_at,
    }
  }

  // Token refresh service
  let tokenRefreshService: TokenRefreshService | null = null

  // Multi-tab sync service
  let multiTabSyncService: MultiTabSyncService | null = null

  // Getters
  const isAuthenticated = computed(() => {
    // Check both user existence and token validity
    return !!user.value && authService.isAuthenticated()
  })
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isOperator = computed(() => user.value?.role === 'operator')
  const isConductor = computed(() => user.value?.role === 'conductor')
  const userCompanyId = computed(() => user.value?.company_id)

  // Watch for user role corruption and attempt to restore from localStorage
  watch(
    () => user.value,
    async (newUser, oldUser) => {
      // Detect if user exists but role is missing (Pinia store installation issue)
      if (newUser && !newUser.role && oldUser && oldUser.role) {
        await nextTick()

        // Try to restore from localStorage
        const storedUser = authService.getStoredUser()
        if (storedUser && storedUser.role) {
          user.value = createUserCopy(storedUser)
        }
      }
    },
    { deep: true, immediate: false },
  )

  // Initialize token refresh service
  const initializeTokenRefresh = () => {
    if (tokenRefreshService) {
      tokenRefreshService.stop()
    }

    tokenRefreshService = createTokenRefreshService(
      {
        refreshMinutesBefore: 10,
        maxRetries: 3,
        retryDelayMs: 1000,
        enableLogging: import.meta.env.DEV,
      },
      {
        refreshTokenApi: authService.refreshToken,
        onRefreshSuccess: async (response: RefreshTokenResponse) => {
          // Update stored tokens
          authService.updateTokens(response)

          // Notify session timeout system about token refresh
          if (typeof window !== 'undefined' && (window as any).__sessionTimeout) {
            ;(window as any).__sessionTimeout.trackTokenRefresh()
          }
        },
        onRefreshFailure: async (error: Error) => {
          console.error('Token refresh failed:', error)
          // Force logout on refresh failure
          await logout('unauthorized')
        },
        onRefreshAttempt: () => {
          // Optional: could show a subtle loading indicator
        },
        onCSRFTokenUpdate: (csrfToken: string) => {
          // Broadcast CSRF token update to other tabs
          multiTabSyncService?.broadcastCSRFTokenUpdate(csrfToken)
        },
      },
    )
  }

  // Start token refresh monitoring
  const startTokenRefresh = () => {
    const tokenInfo = authService.getTokenInfo()
    if (tokenInfo && authService.isAuthenticated()) {
      if (!tokenRefreshService) {
        initializeTokenRefresh()
      }
      tokenRefreshService?.start(tokenInfo.token, tokenInfo.refreshToken)
    }
  }

  // Stop token refresh monitoring
  const stopTokenRefresh = () => {
    tokenRefreshService?.stop()
  }

  // Initialize multi-tab sync service
  const initializeMultiTabSync = () => {
    if (multiTabSyncService) {
      multiTabSyncService.stop()
    }

    multiTabSyncService = createMultiTabSyncService({
      onTokenUpdate: (_token: string, _refreshToken: string) => {
        // Update local state when tokens change in other tabs
        const tokenInfo = authService.getTokenInfo()
        if (tokenInfo) {
          // Restart token refresh with new tokens
          startTokenRefresh()
        }
      },
      onLogout: () => {
        // Another tab logged out, sync local state
        user.value = null
        stopTokenRefresh()
      },
      onUserUpdate: (userData: User) => {
        // Update user data when changed in other tabs
        // Only update if we have valid user data with role
        if (userData && userData.role) {
          user.value = createUserCopy(userData)
        }
      },
      onCSRFTokenUpdate: (_csrfToken: string) => {
        // CSRF token updated in another tab - no additional action needed
        // The token is already stored by the CSRF service
      },
    })
  }

  // Start multi-tab sync
  const startMultiTabSync = () => {
    if (!multiTabSyncService) {
      initializeMultiTabSync()
    }
    multiTabSyncService?.start()
  }

  // Stop multi-tab sync
  const stopMultiTabSync = () => {
    multiTabSyncService?.stop()
  }

  // Actions
  async function login(credentials: LoginRequest) {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)

      // Create a safe copy to ensure all properties are preserved
      user.value = createUserCopy(response.user)

      // Ensure authentication state is properly set before proceeding
      await new Promise((resolve) => setTimeout(resolve, 0))

      // Start token refresh monitoring
      startTokenRefresh()

      // Start multi-tab sync
      startMultiTabSync()

      return response
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed'
      error.value = errorMessage
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout(reason?: 'manual' | 'session_timeout' | 'unauthorized') {
    isLoading.value = true

    try {
      await authService.logout()
    } catch (err) {
      console.warn('Logout error:', err)
    } finally {
      user.value = null
      isLoading.value = false

      // Stop token refresh monitoring
      stopTokenRefresh()

      // Stop multi-tab sync
      stopMultiTabSync()

      // Broadcast logout to other tabs
      multiTabSyncService?.broadcastLogout()

      // Clear any session timeout related data
      if (reason === 'session_timeout') {
        console.info('User logged out due to session timeout')
      }
    }
  }

  async function logoutAll() {
    isLoading.value = true

    try {
      await authService.logoutAll()
    } catch (err) {
      console.warn('Logout all error:', err)
    } finally {
      user.value = null
      isLoading.value = false
    }
  }

  async function fetchCurrentUser() {
    if (!authService.isAuthenticated()) {
      return
    }

    isLoading.value = true

    try {
      const fetchedUser = await authService.getCurrentUser()

      // Only update if fetched user has complete data (including role)
      if (fetchedUser && fetchedUser.role) {
        // Create a safe copy to ensure all properties are preserved
        user.value = createUserCopy(fetchedUser)
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user'
      error.value = errorMessage
      // If fetch fails, clear stored data
      await logout('unauthorized')
    } finally {
      isLoading.value = false
    }
  }

  function initializeFromStorage() {
    const storedUser = authService.getStoredUser()
    const hasValidTokens = localStorage.getItem('auth_token') && localStorage.getItem('user')

    if (storedUser && hasValidTokens) {
      // Create a safe copy to ensure all properties are preserved
      user.value = createUserCopy(storedUser)

      // Verify authentication after setting user
      if (authService.isAuthenticated()) {
        // Start token refresh monitoring for existing session
        startTokenRefresh()

        // Start multi-tab sync for existing session
        startMultiTabSync()
      } else {
        // Clear invalid session
        user.value = null
        authService.clearAuthData()
      }
    }
  }

  function canAccessCompany(companyId: number): boolean {
    if (!user.value) return false

    // Admins can access all companies
    if (user.value.role === 'admin') return true

    // Operators and conductors can only access their own company
    return user.value.company_id === companyId
  }

  function clearError() {
    error.value = null
  }

  // Get redirect URL after successful login
  function getPostLoginRedirect(userRole?: string): string {
    // Check for stored intended redirect
    const storedRedirect = localStorage.getItem('intended_redirect')

    if (storedRedirect && storedRedirect !== '/login') {
      localStorage.removeItem('intended_redirect')
      return storedRedirect
    }

    // Default redirect based on user role
    const role = userRole || user.value?.role

    if (role === 'admin') {
      return '/admin/companies'
    } else {
      return '/dashboard'
    }
  }

  return {
    // State
    user,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isAdmin,
    isOperator,
    isConductor,
    userCompanyId,

    // Actions
    login,
    logout,
    logoutAll,
    fetchCurrentUser,
    initializeFromStorage,
    canAccessCompany,
    clearError,
    getPostLoginRedirect,

    // Token refresh
    startTokenRefresh,
    stopTokenRefresh,

    // Multi-tab sync
    startMultiTabSync,
    stopMultiTabSync,

    // CSRF utilities
    getCSRFStatus: () => csrfService.getStatus(),
    isCSRFEnabled: () => csrfService.isEnabled(),
  }
})
